import { useEffect, useState } from "react";
/**
 * V Number form: packing formula (L×W×H in cm)/1000 uses bankers rounding (round half to even); e.g. 23×16×9 → V3.
 * V calculation is done via computeV (SchemaComputeFunction).
 */
import InputRadio from "../../../../lib/inputs/InputRadio";
import InputRadioGroup from "../../../../lib/inputs/InputRadioGroup";
import AdobeNumberField from "../../../../AdobeAriaLib/AdobeNumberField";
import { httpGet } from "../../../../../modules/Backend";
import { Change } from "../Form";
import { EntityNames } from "../../../../../enums/EntityNames";

interface IDimensionModel {
    width: number;
    height: number;
    depth: number;
    vNumber: string;
    description?: string | null;
}

interface IFormVNumber {
    value: string | null;
    label: string;
    onChange: (value: string | null, elementName: string) => void;
    onChangeBatched: (changes: Change[]) => void;
    widthValue?: number | string;
    heightValue?: number | string;
    depthValue?: number | string;
    required?: boolean;
}

export default function FormVNumber({
    value,
    label,
    onChange,
    onChangeBatched,
    widthValue,
    heightValue,
    depthValue,
    required = false
}: IFormVNumber) {
    const [dimensions, setDimensions] = useState<IDimensionModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    useEffect(() => {
        async function fetchStandardSizes() {
            try {
                setLoading(true);
                const result = await httpGet(`api/${EntityNames.volumes}/standardSizes`);
                if (result?.data?.dimensions) {
                    setDimensions(result.data.dimensions);
                }
            } catch (error) {
                console.error("Failed to fetch standard sizes:", error);
            } finally {
                setLoading(false);
            }
        }

        fetchStandardSizes();
    }, []);

    // Check if current value matches any dimension
    useEffect(() => {
        if (value && dimensions.length > 0) {
            const matchingIndex = dimensions.findIndex(dim => {
                const dimValue = JSON.stringify(dim);
                return value === dimValue;
            });
            setSelectedIndex(matchingIndex >= 0 ? matchingIndex : null);
        } else {
            setSelectedIndex(null);
        }
    }, [value, dimensions]);

    // Auto-select radio if width/height/depth match a standard dimension (only if value is not already set)
    useEffect(() => {
        if (dimensions.length > 0 && !value && widthValue != null && heightValue != null && depthValue != null) {
            const width = typeof widthValue === 'string' ? parseFloat(widthValue) : widthValue;
            const height = typeof heightValue === 'string' ? parseFloat(heightValue) : heightValue;
            const depth = typeof depthValue === 'string' ? parseFloat(depthValue) : depthValue;

            if (!isNaN(width) && !isNaN(height) && !isNaN(depth)) {
                const matchingIndex = dimensions.findIndex(dim => {
                    return dim.width === width && dim.height === height && dim.depth === depth;
                });

                if (matchingIndex >= 0) {
                    const dimension = dimensions[matchingIndex];
                    const dimValue = JSON.stringify(dimension);
                    onChange(dimValue, "vNumberStandardSize");
                    setSelectedIndex(matchingIndex);
                }
            }
        }
    }, [dimensions, widthValue, heightValue, depthValue, value, onChange]);

    function handleRadioChange(e: React.ChangeEvent<HTMLInputElement>) {
        const selectedValue = e.target.value;

        const index = parseInt(selectedValue);
        if (!isNaN(index) && index >= 0 && index < dimensions.length) {
            const dimension = dimensions[index];
            const dimValue = JSON.stringify(dimension);
            onChange(dimValue, "vNumberStandardSize");
            setSelectedIndex(index);

            const changes: Change[] = [
                { value: dimension.width, elementName: "width" },
                { value: dimension.height, elementName: "height" },
                { value: dimension.depth, elementName: "depth" }
            ];
            onChangeBatched(changes);
        }
    }

    function handleDimensionChange() {
        // When dimensions are manually edited, clear the standard size selection
        if (selectedIndex !== null) {
            onChange(null, "vNumberStandardSize");
            setSelectedIndex(null);
        }
    }

    function handleWidthChange(val: string) {
        handleDimensionChange();
        const changes: Change[] = [
            { value: val === "" ? null : parseFloat(val), elementName: "width" }
        ];
        onChangeBatched(changes);
    }

    function handleHeightChange(val: string) {
        handleDimensionChange();
        const changes: Change[] = [
            { value: val === "" ? null : parseFloat(val), elementName: "height" }
        ];
        onChangeBatched(changes);
    }

    function handleDepthChange(val: string) {
        handleDimensionChange();
        const changes: Change[] = [
            { value: val === "" ? null : parseFloat(val), elementName: "depth" }
        ];
        onChangeBatched(changes);
    }

    return (
        <div className="form-vnumber__wrapper">
            <div className="margin__top--xxs">
                <InputRadioGroup
                    className=""
                    label={label}
                    required={required}
                    name="vNumberStandardSize"
                    direction="vertical"
                >
                    {dimensions.map((dim, index) => {
                        return (
                            <InputRadio
                                key={index}
                                id={`vNumberStandardSize-${index}`}
                                name="vNumberStandardSize"
                                className={`${index === 0 ? 'margin__top--xxs margin__bottom--xxs' : 'margin__bottom--xxs'}`}
                                value={index.toString()}
                                label={`${dim.vNumber} (${dim.width} x ${dim.height} x ${dim.depth})${dim.description ? ` - ${dim.description}` : ""}`}
                                checked={selectedIndex === index}
                                onChangeCheckBox={handleRadioChange}
                                disabled={loading}
                            />
                        );
                    })}
                </InputRadioGroup>
            </div>
            <div className="grid-x grid-padding-x margin__top--xs">
                <div className="cell small-12 medium-4 margin__bottom--xxs">
                    <AdobeNumberField
                        value={widthValue?.toString() ?? ""}
                        onChangeText={handleWidthChange}
                        label="Width (cm)"
                        visibleLabel={true}
                        required={true}
                        className=""
                        disabled={false}
                        max={undefined}
                        min={0}
                        validateRule={undefined}
                        validationError={undefined}
                    />
                </div>
                <div className="cell small-12 medium-4 margin__bottom--xxs">
                    <AdobeNumberField
                        value={heightValue?.toString() ?? ""}
                        onChangeText={handleHeightChange}
                        label="Height (cm)"
                        visibleLabel={true}
                        required={true}
                        className=""
                        disabled={false}
                        max={undefined}
                        min={0}
                        validateRule={undefined}
                        validationError={undefined}
                    />
                </div>
                <div className="cell small-12 medium-4 margin__bottom--xxs">
                    <AdobeNumberField
                        value={depthValue?.toString() ?? ""}
                        onChangeText={handleDepthChange}
                        label="Depth (cm)"
                        visibleLabel={true}
                        required={true}
                        className=""
                        disabled={false}
                        max={undefined}
                        min={0}
                        validateRule={undefined}
                        validationError={undefined}
                    />
                </div>
            </div>
        </div>
    );
}
